import { Badge, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { Formik } from 'formik';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import AsyncSelect from 'react-select/async';
import _ from 'lodash';
import { userFormValidationFn, userGroups } from '../../utils/userFormUtils';
import { useAuth } from '../../hooks/useAuth';
import { AccountEntitySearch } from '../../api/models/AccountEntity';
import { useApiClient } from '../../hooks/useApiClient';
import Btn from '../Btn/Btn';
import { ReactSelectOption } from '../../types';
import RequiredFormLabel from '../RequiredFormLabel/RequiredFormLabel';
import SelectOptWithSubtitle from '../SelectOptWithSubtitle/SelectOptWithSubtitle';
import CardPlaceholder from '../CardPlaceholder/CardPlaceholder';
import { Markets } from '../../types';
import i18next from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { RoleEnum, UserDTO } from '../../api';
import MarketFilter from '../MarketFilter/MarketFilter';
import toaster from '../../utils/toaster';
import { Storage } from 'aws-amplify';
import { toast } from 'react-toastify';
import { FaDownload, FaTrash } from 'react-icons/fa';
import { GiCheckMark } from 'react-icons/gi';
import { useReporterDocuments } from '../Reporter/useReporterDocuments';
import SpinnerButton from '../SpinnerButton/SpinnerButton';
import { formatDate, randomString } from 'src/utils';
import ReporterDocumentationFileUploader from '../Reporter/ReporterDocumentationFileUploader';
import { hasMissingPendingDocuments } from '../Reporter/reporterUtils';
import { ReporterFilePreviewModal } from '../Reporter';
import { ReporterStatusEnum } from 'src/api/models/Enums';

type AccountSelectOpt = ReactSelectOption & {
	account: AccountEntitySearch;
	subtitle: string;
};

function UserArea({
	initialValues,
	username,
	submitFnFactory,
	loading,
	stringToOption,
	itsMe,
	roles,
	userSales,
	companyDetail,
	referralPartners,
	backofficeUsers,
	allBackofficeUsers,
	handleRoleChange,
	recordTypeAziendaId,
	isReadonly,
	handleReferencePartnerChange,
	documents,
	response
}: any) {
	let { userRole, userReferencePartnerList } = useAuth();
	const { t } = useTranslation();
	const [referenceSalesDealers, setReferenceSalesDealers] = useState<UserDTO[]>([]);
	const { reporterDocuments, initDocuments, handleReporterDocument } = useReporterDocuments(initialValues.role, documents);
	const [showReporterDocModal, setReporterDocModal] = useState(false);
	const [uploadingFileType, setUploadingFileType] = useState<string | null>();
	const reporterDocsRef = useRef<any>(null);
	const [filePreview, setFilePreview] = useState<{ isOpen: boolean; document?: any }>({ isOpen: false });

	useEffect(() => {
		if (!response) return;
		initDocuments(response.role, response?.documents);
	}, [response])

	const uploadReporterFiles = async (data: any) => {
		try {
			const docs = _.cloneDeep(reporterDocuments);
			const filesToUploadList: any = [];

			for (const doc of docs) {
				if (doc.file) {
					const ext = doc.file.name.split('.').pop();
					filesToUploadList.push({
						file: doc.file,
						key: `${data.username}/${randomString(10)}.${ext}`,
						fileType: doc.code,
						fileName: doc.fileName,
					})
				} else if (doc.key) {
					filesToUploadList.push({
						key: doc.key,
						fileType: doc.code,
						fileName: doc.fileName
					});
				}
			}

			const uploadOps: any[] = filesToUploadList.map(obj => {
				return obj.file ? Storage.put(obj.key, obj.file, {
					contentType: obj.file.type,
					bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_USER_DOCUMENTS,
				}) : Promise.resolve();
			})

			await Promise.all(uploadOps);

			data.documents = filesToUploadList.map(item => _.omit(item, 'file'));
		} catch (e) {
			toast.error(i18next.t('error_loading_file'));
		}
	};

	interface SelectOptions {
		value: string;
		label: string;
	}
	const { apiClient, handleApi, handleApiResponse } = useApiClient();
	const [accountSearchOpt, setAccountSearchOpt] =
		useState<AccountSelectOpt | null>(null);

	const accountSearch = _.debounce((input: string, cb) => {
		setTimeout(() => {
			handleApiResponse(
				apiClient.salesforceAccountController.getAccounts({
					q: input,
					record_type: recordTypeAziendaId,
				})
			).then((res) => {
				let list: AccountEntitySearch[] = res.data;

				const opts: AccountSelectOpt[] = list.map((el) => ({
					value: el.Id,
					label: el.Name,
					account: el,
					subtitle: el?.RecordType?.Name || el.Email__c || '',
				}));
				cb(opts);
			});
		});
	}, 500);

	const computeDealerName = (data: any) => {
		const predicates = [
			[_.isString, (d: any) => d],
			[_.isArray, (d: any) => d.find((value: any) => _.isString(value))],
			[(d: any) => _.isObject(d) && !_.isArray(d), (d: any) => d.value],
			[_.stubTrue, _.constant(undefined)],
		];

		return _.cond(predicates)(data);
	}

	const loadReferenceSalesDealers = (data: any, initValues?: any) => {
		let dealerName = computeDealerName(data);

		handleApi(apiClient.userController.getReporterSalesByDealer(dealerName))
			.then((result) => {
				setReferenceSalesDealers(result);

				if (initValues && initValues.reference_sales_dealer) {
					const item = result.find(
						(r) => r.username === initValues.reference_sales_dealer
					);

					if (item) {
						initValues.reference_sales_dealer = {
							value: initValues.reference_sales_dealer,
							label: `${item?.given_name} ${item?.family_name} (${item?.username})`,
						};
					}
				}
			})
			.catch((err) =>
				toaster.error(err.message || i18next.t('error_loading_account_data'))
			);
	};

	useEffect(() => {
		if (
			[RoleEnum.SALES_DEALER, RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER].includes(userRole) &&
			RoleEnum.SEGNALATORE === initialValues.role
		) {
			loadReferenceSalesDealers(userReferencePartnerList, initialValues);

			// userReferencePartnerList?.map((item) => ({
			// 	value: item,
			// 	label: item,
			// })) || [];
		}
	}, [initialValues.role]);

	const salableByOptionsMap = {
		MASERATI: 'MASERATI',
		VOLKSWAGEN: 'VOLKSWAGEN',
		STELLANTIS: 'STELLANTIS',
		STELLANTIS_YOU: 'STELLANTIS&YOU',
		FACILE: 'FACILE',
	}

	const salableByOptions: SelectOptions[] = Object.entries(salableByOptionsMap).map(([value, label]) => ({ value, label }));

	const showSepa = true;
	// const showSepa = [RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER].includes(userRole);

	const downloadTemplate = async (templateDocument) => {
		try {
			const downloadLink: string = await Storage.get(
				templateDocument.filename,
				{ bucket: templateDocument.bucket }
			);
			window.open(downloadLink, '_blank');
		} catch (error) {
			console.warn(error)
      toaster.error(i18next.t('cannot_download_file'));
		}
	}

	return (
		<>
			{loading ? (
				<CardPlaceholder />
			) : (
				<>
					{initialValues && (
						<Card className="mt-2">
							<Card.Body>
								<Formik
									initialValues={initialValues}
									validate={(values) => userFormValidationFn(values, userRole)}
									enableReinitialize // This ensures the form updates with new initialValues
									onSubmit={async (values, formikHelpers) => {
										if (RoleEnum.SEGNALATORE === values.role) {
											if (_.isEmpty(values.iban)) {
												toaster.error(i18next.t('iban_required'));
												return;
											}

											if (hasMissingPendingDocuments(userRole, reporterDocuments)) {
												toaster.error(i18next.t('segnalatore_all_documents_required'));
												return;
											}

											await uploadReporterFiles(values);
										}
										submitFnFactory(username)(values, formikHelpers);
									}}
								>
									{({
										values,
										errors,
										touched,
										handleChange,
										handleBlur,
										handleSubmit,
										isSubmitting,
										setFieldValue,
									}) => {
										let additionalFields;

										{
											/* CAN REASSIGN LEADS */
										}
										if (
											[RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER].includes(
												values.role
											)
										) {
											additionalFields = (
												<Row>
													<Col md={6} className="mt-2">
														<Form.Group className="mb-3">
															<Form.Label>{t('can_reassign_leads')}</Form.Label>
															<Form.Check
																name="can_reassign_leads"
																type="switch"
																onChange={handleChange}
																onBlur={handleBlur}
																value={values.can_reassign_leads}
																checked={values.can_reassign_leads}
																disabled={itsMe || userRole !== RoleEnum.MASTER}
																className="hor-form-check-switch"
															/>
														</Form.Group>
													</Col>
												</Row>
											);
										}
										// if (!values.role || values.role === RoleEnum.BACKOFFICE) {
										// 	additionalFields = null;
										// }
										if (
											[
												RoleEnum.SALES,
												RoleEnum.SALES_DEALER,
												RoleEnum.SALES_INTERNAL,
												RoleEnum.SEGNALATORE,
											].includes(values.role)
										) {
											additionalFields = (
												<>
													<Row>
														<Col md={6}>
															<Form.Group className="mb-3">
																<Form.Label>{t('phone_backup')}</Form.Label>
																<Form.Control
																	onChange={handleChange}
																	onBlur={handleBlur}
																	// value={values.phone_backup}
																	value={values.phone_backup || ''}
																	name="phone_backup"
																	type="text"
																/>
															</Form.Group>
														</Col>
														<Col md={6}>
															<Form.Group className="mb-3">
																<Form.Label>{t('email_backup')}</Form.Label>
																<Form.Control
																	onChange={handleChange}
																	onBlur={handleBlur}
																	value={values.email_backup}
																	name="email_backup"
																	type="text"
																/>
															</Form.Group>
														</Col>
													</Row>

													{values.role !== RoleEnum.SEGNALATORE && (
														<Row>
															<Col md={3}>
																<Form.Group className="mb-3">
																	<Form.Label>
																		{t('salesforce_seller')}:{' '}
																		{values.is_salesforce_sales ? 'Si' : 'No'}
																	</Form.Label>
																	<Form.Check
																		name="is_salesforce_sales"
																		type="switch"
																		onChange={handleChange}
																		onBlur={handleBlur}
																		value={values.is_salesforce_sales}
																		checked={values.is_salesforce_sales}
																		disabled={
																			userRole !== RoleEnum.MASTER ||
																			userRole === RoleEnum.BACKOFFICE ||
																			userRole === RoleEnum.AREA_MANAGER
																		}
																		className="hor-form-check-switch"
																	/>
																</Form.Group>
															</Col>

															{![
																RoleEnum.SALES,
																RoleEnum.SALES_DEALER,
																RoleEnum.SALES_INTERNAL,
															].includes(userRole) && (
																	<Col md={3}>
																		<Form.Group className="mb-3">
																			<Form.Label>
																				{t('display_commissions')}
																			</Form.Label>
																			<Form.Check
																				name="display_commissions"
																				type="switch"
																				onChange={handleChange}
																				onBlur={handleBlur}
																				value={values.display_commissions}
																				checked={values.display_commissions}
																				disabled={itsMe}
																				className="hor-form-check-switch"
																			/>
																		</Form.Group>
																	</Col>
																)}
														</Row>
													)}
												</>
											);
										}

										return (
											<Form onSubmit={handleSubmit}>
												<fieldset disabled={isReadonly}>
													<Row>
														<Col md={6}>
															<Form.Group className="mb-3">
																<Form.Label>
																	{t('username')} ({t('email')})
																	<span className="red-ast">*</span>
																</Form.Label>
																<Form.Control
																	onChange={handleChange}
																	onBlur={handleBlur}
																	value={values.username}
																	name="username"
																	type="text"
																	disabled={itsMe ? true : false}
																	className={`${errors.username && touched.username
																		? 'error-border'
																		: ''
																		}  ${itsMe ? 'grey-out' : ''}`}
																/>
																{errors.username && touched.username && (
																	<small className="text-danger">
																		{errors.username as string}
																	</small>
																)}
															</Form.Group>
														</Col>

														{/* RAGIONE SOCIALE */}
														{values.role === RoleEnum.FLEET_MANAGER && (
															<Col md={6}>
																<Form.Group className="mb-3">
																	<Form.Label>{t('business_name')}</Form.Label>
																	<span className="red-ast">*</span>
																	<AsyncSelect
																		menuPosition="fixed"
																		isDisabled={isReadonly}
																		isClearable
																		placeholder={t('select_a_company_account')}
																		// Ensure that the value prop is in the correct format (object with `value` and `label`)
																		value={
																			values.business_name
																				? values.business_name
																				: accountSearchOpt
																		}
																		// Provide default options if applicable
																		defaultOptions={
																			accountSearchOpt ? [accountSearchOpt] : []
																		}
																		loadOptions={accountSearch}
																		// Handle option selection and set it in form state
																		onChange={(opt: any) => {
																			// Update business_name to the selected value (which is an object)
																			setFieldValue(
																				'business_name',
																				opt || null
																			);
																			setAccountSearchOpt(opt); // Update local state if needed
																		}}
																		// Define the shape of options
																		components={{
																			Option: (props: any) => {
																				return (
																					<SelectOptWithSubtitle {...props} />
																				);
																			},
																		}}
																		name="business_name"
																	/>
																</Form.Group>
															</Col>
														)}

														{/* ROLE */}
														{userRole !== RoleEnum.SALES_DEALER && (
															<Col md={6}>
																<Form.Group className="mb-3">
																	<Form.Label>
																		{t('role')}
																		<span className="red-ast">*</span>
																	</Form.Label>

																	<Select
																		menuPosition="fixed"
																		value={stringToOption(values.role)}
																		onBlur={handleBlur}
																		onChange={(opt) => {
																			handleRoleChange(
																				opt?.value,
																				setFieldValue,
																				values
																			)
																			initDocuments(opt?.value, response);
																			reporterDocsRef.current?.clear()
																		}}
																		name="role"
																		options={roles}
																		isDisabled={
																			isReadonly ||
																			[
																				RoleEnum.SALES,
																				RoleEnum.SALES_DEALER,
																				RoleEnum.SALES_INTERNAL,
																				RoleEnum.FLEET_MANAGER,
																			].includes(userRole)
																		}
																	/>

																	{errors.role && touched.role && (
																		<small className="text-danger">
																			{errors.role as string}
																		</small>
																	)}
																</Form.Group>
															</Col>
														)}

														{/* RAGIONE SOCIALE SEGNALATORE */}
														{values.role === RoleEnum.SEGNALATORE && (
															<Col md={6}>
																<Form.Group className="mb-3">
																	<Form.Label>{t('business_name')}</Form.Label>
																	<span className="red-ast">*</span>
																	<Form.Control
																		onChange={handleChange}
																		onBlur={handleBlur}
																		value={values.business_name}
																		name="business_name"
																		type="text"
																		className={`${errors.business_name && touched.business_name ? 'error-border' : '' }`}
																	/>
																	{errors.business_name && touched.business_name && (
																		<small className="text-danger">
																			{errors.business_name as string}
																		</small>
																	)}
																</Form.Group>
															</Col>
														)}

														<Col md={6}>
															<Form.Group className="mb-3">
																<Form.Label>
																	{values.role === RoleEnum.SEGNALATORE ? t('ref_user_name') : t('name')}
																	<span className="red-ast">*</span>
																</Form.Label>
																<Form.Control
																	onChange={handleChange}
																	onBlur={handleBlur}
																	value={values.given_name}
																	name="given_name"
																	type="text"
																	className={`${errors.given_name && touched.given_name
																		? 'error-border'
																		: ''
																		}`}
																/>
																{errors.given_name && touched.given_name && (
																	<small className="text-danger">
																		{errors.given_name as string}
																	</small>
																)}
															</Form.Group>
														</Col>

														<Col md={6}>
															<Form.Group className="mb-3">
																<Form.Label>
																	{values.role === RoleEnum.SEGNALATORE ? t('ref_user_surname') : t('surname')}
																	<span className="red-ast">*</span>
																</Form.Label>
																<Form.Control
																	onChange={handleChange}
																	onBlur={handleBlur}
																	value={values.family_name}
																	name="family_name"
																	type="text"
																	className={`${errors.family_name && touched.family_name
																		? 'error-border'
																		: ''
																		}`}
																/>
																{errors.family_name && touched.family_name && (
																	<small className="text-danger">
																		{errors.family_name as string}
																	</small>
																)}
															</Form.Group>
														</Col>

														{values.role === RoleEnum.SEGNALATORE && (
															<>
																<Col md={6}>
																	<Form.Group className="mb-3">
																		<Form.Label>
																			{t('fiscal_code')}
																			{!values.partita_iva && (
																				<span className="red-ast">*</span>
																			)}
																		</Form.Label>
																		<Form.Control
																			onChange={handleChange}
																			onBlur={handleBlur}
																			value={values.codice_fiscale}
																			name="codice_fiscale"
																			type="text"
																			className={`${errors.codice_fiscale &&
																				touched.codice_fiscale
																				? 'error-border'
																				: ''
																				}`}
																		/>
																		{errors.codice_fiscale &&
																			touched.codice_fiscale && (
																				<small className="text-danger">
																					{errors.codice_fiscale as string}
																				</small>
																			)}
																	</Form.Group>
																</Col>

																<Col md={6}>
																	<Form.Group className="mb-3">
																		<Form.Label>
																			{t('business_fiscal_code')}
																			{!values.codice_fiscale && (
																				<span className="red-ast">*</span>
																			)}
																		</Form.Label>
																		<Form.Control
																			onChange={handleChange}
																			onBlur={handleBlur}
																			value={values.partita_iva}
																			name="partita_iva"
																			type="text"
																			className={`${errors.partita_iva &&
																				touched.partita_iva
																				? 'error-border'
																				: ''
																				}`}
																		/>
																		{errors.partita_iva &&
																			touched.partita_iva && (
																				<small className="text-danger">
																					{errors.partita_iva as string}
																				</small>
																			)}
																	</Form.Group>
																</Col>
															</>
														)}

														{values.role === RoleEnum.FLEET_MANAGER && (
															<Col md={6}>
																<Form.Group className="mb-3">
																	<Form.Label>{t('sales')}</Form.Label>
																	<Select
																		menuPosition="fixed"
																		value={values.sales}
																		onBlur={handleBlur}
																		onChange={(opt) => {
																			setFieldValue('sales', opt || []);
																		}}
																		isMulti={true}
																		name="sales"
																		options={userSales}
																	/>
																</Form.Group>
															</Col>
														)}

														<Col md={6}>
															<Form.Group className="mb-3">
																<Form.Label>
																	{t('phone')}
																	{values.role === RoleEnum.SEGNALATORE && (
																		<span className="red-ast">*</span>
																	)}
																</Form.Label>
																<Form.Control
																	onChange={handleChange}
																	onBlur={handleBlur}
																	value={values.phone}
																	className={`${errors.phone && touched.phone
																		? 'error-border'
																		: ''
																		}`}
																	name="phone"
																	type="text"
																/>
																{errors.phone && touched.phone && (
																	<small className="text-danger">
																		{errors.phone as string}
																	</small>
																)}
															</Form.Group>
														</Col>

														{[
															RoleEnum.SALES,
															RoleEnum.SALES_DEALER,
															RoleEnum.SALES_INTERNAL,
															RoleEnum.SEGNALATORE,
															RoleEnum.MASTER,
															RoleEnum.BACKOFFICE,
															RoleEnum.AREA_MANAGER,
														].includes(values.role) && (
																<Col md={6}>
																	<Form.Group className="mb-3">
																		<RequiredFormLabel
																			label={
																				values.role === RoleEnum.SALES_INTERNAL
																					? i18next.t('reference_branch_label')
																					: i18next.t('dealer')
																			}
																		/>
																		<Select
																			menuPosition="fixed"
																			isMulti
																			value={values.reference_partner}
																			onBlur={handleBlur}
																			onChange={(opt) => {
																				if (
																					RoleEnum.SEGNALATORE === values.role &&
																					[
																						RoleEnum.SALES_DEALER,
																						RoleEnum.MASTER,
																						RoleEnum.BACKOFFICE,
																						RoleEnum.AREA_MANAGER,
																					].includes(userRole)
																				) {
																					setFieldValue('reference_partner', [opt.at(-1)]);
																					setFieldValue('reference_sales_dealer', null);
																					loadReferenceSalesDealers(opt.at(-1));
																				} else if (
																					[
																						RoleEnum.MASTER,
																						RoleEnum.BACKOFFICE,
																						RoleEnum.AREA_MANAGER,
																					].includes(userRole)
																				) {
																					if (
																						[
																							RoleEnum.SALES,
																							RoleEnum.SALES_DEALER,
																							RoleEnum.SALES_INTERNAL,
																							RoleEnum.SEGNALATORE,
																						].includes(values.role)
																					) {
																						setFieldValue('reference_partner', [
																							opt.at(-1),
																						]);
																						handleReferencePartnerChange?.(
																							opt.at(-1)?.value,
																							setFieldValue,
																							values
																						);
																					} else {
																						setFieldValue(
																							'reference_partner',
																							opt
																						);
																					}
																				}
																			}}
																			name="reference_partner"
																			noOptionsMessage={() =>
																				`${i18next.t('loading')}...`
																			}
																			options={
																				[
																					RoleEnum.SALES_DEALER,
																					RoleEnum.BACKOFFICE,
																					RoleEnum.AREA_MANAGER,
																				].includes(userRole) &&
																					RoleEnum.SEGNALATORE === values.role
																					? userReferencePartnerList?.map(
																						(item) => ({
																							value: item,
																							label: item,
																						})
																					) || []
																					: referralPartners
																			}
																			isDisabled={
																				isReadonly ||
																				![
																					RoleEnum.MASTER,
																					RoleEnum.BACKOFFICE,
																					RoleEnum.AREA_MANAGER,
																				].includes(userRole)
																			}
																		// La modifica del partner di riferimento deve essere sempre
																		// possibile per tutti gli account che possono creare un master
																		/>
																		{errors.reference_partner &&
																			touched.reference_partner && (
																				<small className="text-danger">
																					{errors.reference_partner as string}
																				</small>
																			)}
																	</Form.Group>
																</Col>
															)}

														{[RoleEnum.SEGNALATORE].includes(values.role) && (
															<>
																<Col md={6}>
																	<Form.Group className="mb-3">
																		<RequiredFormLabel
																			label={i18next.t('reference_user') + '  Dealer'}
																		/>
																		<Select
																			menuPosition="fixed"
																			isDisabled={
																				!values.reference_partner?.length
																			}
																			value={values.reference_sales_dealer}
																			onBlur={handleBlur}
																			onChange={(opt) => {
																				setFieldValue(
																					'reference_sales_dealer',
																					opt
																				);
																			}}
																			name="reference_sales_dealer"
																			noOptionsMessage={() =>
																				`${i18next.t('loading')}`
																			}
																			options={referenceSalesDealers?.map(
																				(item) => ({
																					value: item.username,
																					label: `${item.given_name} ${item.family_name} (${item.username})`,
																				})
																			)}
																		/>
																		{errors.reference_sales_dealer &&
																			touched.reference_sales_dealer && (
																				<small className="text-danger">
																					{
																						errors.reference_sales_dealer as string
																					}
																				</small>
																			)}
																	</Form.Group>
																</Col>
																{userRole !== RoleEnum.SALES_DEALER && (
																	<Col md={6}>
																		<Form.Group className="mb-3">
																			<RequiredFormLabel
																				label={i18next.t('reference_backoffice')}
																			/>
																			<Select
																				menuPosition="fixed"
																				value={values.reference_backoffice}
																				onBlur={handleBlur}
																				onChange={(opt) => {
																					setFieldValue(
																						'reference_backoffice',
																						opt
																					);
																				}}
																				name="reference_backoffice"
																				noOptionsMessage={() =>
																					`${i18next.t('loading')}`
																				}
																				options={allBackofficeUsers}
																				components={{
																					Option: (props: any) => {
																						return (
																							<SelectOptWithSubtitle
																								{...props}
																							></SelectOptWithSubtitle>
																						);
																					},
																				}}
																			/>
																			{errors.reference_backoffice &&
																				touched.reference_backoffice && (
																					<small className="text-danger">
																						{
																							errors.reference_backoffice as string
																						}
																					</small>
																				)}
																		</Form.Group>
																	</Col>
																)}
															</>
														)}

														{[
															RoleEnum.SALES,
															RoleEnum.SALES_DEALER,
															RoleEnum.SALES_INTERNAL,
														].includes(values.role) && (
																<>
																	{/* REFERENCE BACKOFFICE MULTISELECT */}
																	<Col md={6}>
																		<Form.Group className="mb-3">
																			<RequiredFormLabel
																				label={i18next.t('reference_user')}
																			/>
																			<Select
																				menuPosition="fixed"
																				isDisabled={
																					![
																						RoleEnum.MASTER,
																						RoleEnum.BACKOFFICE,
																					].includes(userRole)
																				}
																				value={values.reference_backoffice}
																				onBlur={handleBlur}
																				onChange={(opt) => {
																					setFieldValue(
																						'reference_backoffice',
																						opt
																					);
																				}}
																				name="reference_backoffice"
																				noOptionsMessage={() =>
																					`${i18next.t('loading')}`
																				}
																				options={backofficeUsers}
																				components={{
																					Option: (props: any) => {
																						return (
																							<SelectOptWithSubtitle
																								{...props}
																							></SelectOptWithSubtitle>
																						);
																					},
																				}}
																			/>
																			{errors.reference_backoffice &&
																				touched.reference_backoffice && (
																					<small className="text-danger">
																						{
																							errors.reference_backoffice as string
																						}
																					</small>
																				)}
																		</Form.Group>
																	</Col>

																	{values.role !== RoleEnum.SEGNALATORE && (
																		<MarketFilter markets={[Markets.it]}>
																			{/* VISIBILITY TAG */}
																			<Col md={6}>
																				<Form.Group className="mb-3">
																					<Form.Label>
																						{t('visibility_tag')}
																					</Form.Label>
																					<Select
																						menuPosition="fixed"
																						value={values?.visibility_tag?.map(
																							(tag) => ({
																								value: tag,
																								label: salableByOptionsMap[tag] || tag,
																							})
																						)}
																						onBlur={handleBlur}
																						onChange={(opt) => {
																							const visibilityTagsValue = opt.map(
																								(item) => item.value
																							);
																							setFieldValue(
																								'visibility_tag',
																								visibilityTagsValue?.length ? [_.last(visibilityTagsValue)] : []
																							);
																						}}
																						isMulti={true}
																						name="visibility_tag"
																						options={salableByOptions}
																						isDisabled={userRole !== 'MASTER'}
																					/>
																				</Form.Group>
																			</Col>
																		</MarketFilter>
																	)}
																</>
															)}
														{values.role === RoleEnum.FLEET_MANAGER && (
															<Col md={6}>
																<Form.Group className="mb-3">
																	<Form.Label>{t('typology')}</Form.Label>

																	<Select
																		menuPosition="fixed"
																		value={values.company_detail}
																		onBlur={handleBlur}
																		onChange={(opt) => {
																			setFieldValue('company_detail', opt);
																		}}
																		name="company_detail"
																		options={companyDetail}
																	/>
																	{errors.role && touched.role && (
																		<small className="text-danger">
																			{errors.role as string}
																		</small>
																	)}
																</Form.Group>
															</Col>
														)}

														{values.role === RoleEnum.FLEET_MANAGER && (
															<Col md={6}>
																<Form.Group className="mb-3">
																	<Form.Label>
																		{values.is_active_email
																			? i18next.t('email_notifications')
																			: i18next.t('no_email_notfications')}
																	</Form.Label>
																	<Form.Check
																		name="is_active_email"
																		type="switch"
																		onChange={handleChange}
																		onBlur={handleBlur}
																		value={values.is_active_email}
																		checked={values.is_active_email}
																		disabled={itsMe}
																		className="hor-form-check-switch"
																	/>
																</Form.Group>
															</Col>
														)}

														{values.role === RoleEnum.AREA_MANAGER && (
															<Col md={6}>
																<Form.Group className="mb-3">
																	<RequiredFormLabel label={'Backoffice'} />
																	<Select
																		menuPosition="fixed"
																		isMulti
																		value={values.area_backoffices || []}
																		onBlur={handleBlur}
																		onChange={(opt) => {
																			setFieldValue(
																				'area_backoffices',
																				opt || []
																			);
																		}}
																		name="area_backoffices"
																		noOptionsMessage={() =>
																			`${i18next.t('loading')}...`
																		}
																		options={backofficeUsers}
																		placeholder={t('select')}
																		isDisabled={
																			isReadonly ||
																			![
																				RoleEnum.MASTER,
																				RoleEnum.BACKOFFICE,
																				RoleEnum.AREA_MANAGER,
																			].includes(userRole)
																		}
																	/>
																	{errors.area_backoffices &&
																		touched.area_backoffices && (
																			<small className="text-danger">
																				{errors.area_backoffices as string}
																			</small>
																		)}
																</Form.Group>
															</Col>
														)}
													</Row>

													{values.role === RoleEnum.SEGNALATORE && (
														<Row>
															<Col md={6}>
																<Form.Group className="mb-3">
																	<Form.Label>
																		{t('reporter_commissions_perc')}
																		<span className="red-ast">*</span>
																	</Form.Label>
																	<Form.Control
																		onChange={(e) => {
																			const value = e.target.value;
																			const valueAsNumber = (Number(value) || 0);
																			if (valueAsNumber > 100 || valueAsNumber < 0) {
																				return;
																			}
																			setFieldValue(
																				'provvigioni_dealer',
																				100 - valueAsNumber
																			);

																			handleChange(e);
																		}}
																		onBlur={handleBlur}
																		value={values.provvigioni_segnalatore}
																		name="provvigioni_segnalatore"
																		type="number"
																		max={100}
																		min={0}
																		step="any"
																		className={`${errors.provvigioni_segnalatore &&
																			touched.provvigioni_segnalatore
																			? 'error-border'
																			: ''
																			}`}
																	/>
																	{errors.provvigioni_segnalatore &&
																		touched.provvigioni_segnalatore && (
																			<small className="text-danger">
																				{
																					errors.provvigioni_segnalatore as string
																				}
																			</small>
																		)}
																</Form.Group>
															</Col>
															<Col md={6}>
																<Form.Group className="mb-3">
																	<Form.Label>
																		{t('dealer_commissions_perc')}
																	</Form.Label>
																	<Form.Control
																		disabled={true}
																		onChange={handleChange}
																		onBlur={handleBlur}
																		value={values.provvigioni_dealer}
																		name="provvigioni_dealer"
																		type="number"
																		max={100}
																		min={0}
																		step="any"
																		className={`${errors.provvigioni_dealer &&
																			touched.family_name
																			? 'error-border'
																			: ''
																			}`}
																	/>
																	{errors.provvigioni_dealer &&
																		touched.provvigioni_dealer && (
																			<small className="text-danger">
																				{errors.provvigioni_dealer as string}
																			</small>
																		)}
																</Form.Group>
															</Col>
														</Row>
													)}

													{additionalFields}

													{values.role !== RoleEnum.SEGNALATORE && (
														<Row>
															{/* IS ACTIVE FLAG */}
															{username !== 'new' && (
																<Col md={6}>
																	<Form.Group className="mb-3">
																		<Form.Label>
																			{t('notification_email')}
																			{/* {values.is_active ? i18next.t('active') : i18next.t('inactive')} */}
																		</Form.Label>
																		<Form.Check
																			name="is_active"
																			type="switch"
																			onChange={handleChange}
																			onBlur={handleBlur}
																			value={values.is_active}
																			checked={values.is_active}
																			disabled={itsMe}
																			className="hor-form-check-switch"
																		/>
																	</Form.Group>
																</Col>
															)}
														</Row>
													)}

													{values.role === RoleEnum.SEGNALATORE && (
														<Row>
															<Col md={6}>
																<Form.Group className="mb-3">
																	<RequiredFormLabel
																		label={i18next.t('iban')}
																	/>
																	<Form.Control
																		onChange={handleChange}
																		onBlur={handleBlur}
																		value={values.iban || ''}
																		name="iban"
																		type="text"
																	/>
																</Form.Group>
															</Col>
														</Row>
													)}

													{values.role === RoleEnum.SEGNALATORE && (
														<div className="my-3 pb-3">
															<hr />
															<h6>{i18next.t('documents')}<span className="red-ast">*</span></h6>
															{reporterDocuments
																.filter(doc => showSepa ? true : doc.code !== 'MODULO_SEPA_FIRMATO')
																.map((doc, index) => (<Row key={index} className="align-items-center mb-2">
																	<Col md="auto">
																		{false ? (
																			<SpinnerButton busy={false} label="" />
																		) : (
																			<>
																				<div className="d-flex align-items-center">
																					{doc.isUploaded
																						? <GiCheckMark className="text-success me-2" />
																						: <span style={{width: 24}}></span>
																					}
																					{doc.templateDocument
																					? 	<Button
																						variant="link"
																						className="m-0 p-0"
																						onClick={async () => downloadTemplate(doc.templateDocument)}
																					>
																						<span className="me-2">{doc.label}</span>
																						<FaDownload className="text-black" />
																					</Button>
																					: <span className="me-2">{doc.label}</span>}
																				</div>
																				{doc.isUploaded ? (
																					<span
																						className={`${doc.key && 'text-decoration-underline'} ms-4`}
																						style={{ cursor: doc.key && 'pointer' }}
																						onClick={async () => {
																							if (!doc.key) return;
																							const downloadLink: string = await Storage.get(doc.key, {
																								bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_USER_DOCUMENTS,
																							});
																							setFilePreview({ isOpen: true, document: { ...doc, url: downloadLink } })
																						}}
																					>
																						({doc.fileName})
																					</span>
																				) : null}
																			</>
																		)}
																	</Col>

																	<Col className="ms-auto" md="auto">
																		{true && doc.createdAt
																			? <Badge bg="secondary" className="m-2">{formatDate(doc.createdAt, 'it-IT', true)}</Badge>
																			: null
																		}
																		{doc.key && (
																			<Button
																				variant="link"
																				onClick={async () => {
																					const downloadLink: string = await Storage.get(doc.key, {
																						bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_USER_DOCUMENTS,
																					});
																					window.open(downloadLink, '_blank');
																				}}
																			>
																				<FaDownload />
																			</Button>)
																		}
																		<Btn
																			onClick={() => {
																				setUploadingFileType(doc.code);
																				setReporterDocModal(true)
																			}}
																		>
																			<SpinnerButton label={i18next.t('upload')} busy={false} />
																		</Btn>
																	</Col>
																</Row>))}
															{/* <ListGroup variant="flush" className="mb-4">
																{userDocuments?.map((item, idx) => (
																	<ListGroup.Item key={idx} as="li" className="d-flex justify-content-between align-items-start">
																		<div className="me-auto">{item?.fileName}</div>
																		<div>
																			<Button
																				disabled={!item?.key}
																				variant="link"
																				onClick={async () => {
																					const downloadLink: string = await Storage.get(item.key, {
																						bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_USER_DOCUMENTS,
																					});
																					window.open(downloadLink, '_blank');
																				}}
																			>
																				<FaDownload />
																			</Button>
																			<Button
																				variant="link"
																				onClick={() => {
																					setUserDocuments(list => list?.filter(doc => doc?.fileName !== item?.fileName))
																				}}>
																				<FaTrash />
																			</Button>
																		</div>
																	</ListGroup.Item>
																))}
															</ListGroup> */}
															<hr />
														</div>
													)}

													{userRole === RoleEnum.MASTER && [RoleEnum.MASTER, RoleEnum.AREA_MANAGER, RoleEnum.BACKOFFICE].includes(values.role) && (
														<Col md={6}>
															<Form.Group className="mb-3">
																<Form.Label>
																	{t('user_group')}
																</Form.Label>
																<Select
																	isClearable
																	isMulti
																	menuPosition="fixed"
																	onBlur={handleBlur}
																	name="group"
																	value={values.group || []}
																	onChange={(opt) => {
																		setFieldValue('group', opt || []);
																	}}
																	options={userGroups}
																/>
															</Form.Group>
														</Col>
													)}

													{!isReadonly && (
														<Btn
															type="submit"
															icon="arrow-right"
															className="float-end"
															disabled={isSubmitting}
														>
															{username === 'new'
																? i18next.t('create')
																: i18next.t('update')}
														</Btn>
													)}
												</fieldset>
											</Form>
										);
									}}
								</Formik>
							</Card.Body>
							{showReporterDocModal && (
								<ReporterDocumentationFileUploader
									ref={reporterDocsRef}
									handleReporterDocument={(file) => handleReporterDocument(uploadingFileType, file)}
									show={showReporterDocModal}
									onHide={() => {
										setReporterDocModal(false);
										setUploadingFileType(null);
									}}
									onSuccess={() => {
										setReporterDocModal(false);
										setUploadingFileType(null);
									}}
								/>
							)}
							<ReporterFilePreviewModal
								isOpen={filePreview.isOpen}
								closePreview={() => setFilePreview({ isOpen: false })}
								document={filePreview.document}
							/>
						</Card>
					)}
				</>
			)
			}
		</>
	);
}

export default UserArea;
